import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fourPillars } from '@/mocks/curriculumData';

const pillarImages = [
  '/images/generated/pillars-training-2026.jpg',
  '/images/generated/pillars-materials-2026.jpg',
  '/images/generated/pillars-skills-2026.jpg',
  '/images/generated/pillars-evaluation-2026.jpg',
];

export default function FourPillars() {
  const { t } = useTranslation();

  return (
    <section className="relative py-20 md:py-28 bg-background-100/50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          {String(t('curriculum.pillars.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('curriculum.pillars.title')}
          </h2>
          {String(t('curriculum.pillars.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto"></div>
          {String(t('curriculum.pillars.description')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-500 tracking-[0.04em] leading-relaxed max-w-[650px] mx-auto mt-5">{__s}</p>))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {fourPillars.pillars.map((pillar, idx) => {
            const content = (
              <>
                <div className="relative w-full aspect-[4/3] overflow-hidden mb-4">
                  <img
                    src={pillarImages[idx]}
                    alt={t(`curriculum.pillars.${idx}.title`)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-100/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/90 text-primary-900">
                      <i className={`${pillar.icon} text-base`}></i>
                    </div>
                  </div>
                </div>
                <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-1 group-hover:text-accent-600 transition-colors">
                  {t(`curriculum.pillars.${idx}.title`)}
                </h3>
                <p className="text-[12px] text-accent-500/70 tracking-[0.1em] mb-2">
                  {t(`curriculum.pillars.${idx}.titleEn`)}
                </p>
                <p className="text-[14px] text-foreground-500 tracking-[0.03em] leading-relaxed mb-3">
                  {t(`curriculum.pillars.${idx}.description`)}
                </p>
                <div className="flex items-center gap-2 text-accent-500 group-hover:text-accent-400 transition-colors mt-auto">
                  <span className="text-[13px] tracking-[0.06em]">{t('curriculum.pillars.viewLink')}</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </div>
              </>
            );

            if (pillar.isAnchor) {
              return (
                <a
                  key={idx}
                  href={pillar.href}
                  className="card-glow group bg-background-50 border border-background-200/60 p-4 flex flex-col cursor-pointer"
                >
                  {content}
                </a>
              );
            }

            return (
              <Link
                key={idx}
                to={pillar.href}
                className="card-glow group bg-background-50 border border-background-200/60 p-4 flex flex-col cursor-pointer"
              >
                {content}
              </Link>
            );
          })}
        </div>

        {/* Evaluation note for anchor target */}
        <div id="evaluation-note" className="mt-14 p-6 bg-background-50 border border-background-200/60 max-w-[800px] mx-auto">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-accent-500 text-sm"></i>
            </div>
            <div>
              <h4 className="font-heading text-[15px] text-foreground-900 tracking-[0.05em] mb-2">
                {t('curriculum.pillars.evalNote.title')}
              </h4>
              {String(t('curriculum.pillars.evalNote.text')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[14px] text-foreground-500 tracking-[0.03em] leading-relaxed">{__s}</p>))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
